import { useCallback, useState, useTransition } from "react";

/** Forward/backward cursor variables for a Relay connection page fetch. */
export interface CursorVariables {
  first: number | null;
  last: number | null;
  after: string | null;
  before: string | null;
}

/** The cursor flags the hook reads from the currently loaded connection page. */
interface PageInfoLike {
  startCursor?: string | null;
  endCursor?: string | null;
}

/** Params for {@link useCursorPagination} */
interface UseCursorPaginationParams {
  /** pageInfo of the currently loaded page, for its start/end cursors */
  pageInfo: PageInfoLike;
  /** Real rows on the current page, used for the 1-based range summary */
  rowCount: number;
  /** Page size to start at */
  defaultPageSize: number;
  /**
   * Runs the actual refetch for a page. The hook passes every cursor variable
   * with the unused direction explicitly nulled, so a prior direction can never
   * leak through Relay's variable merging. Callers own the rest here: the typed
   * refetch, any extra variables (e.g. a filter) and the fetch policy.
   */
  onNavigate: (variables: CursorVariables) => void;
  /**
   * Runs before each navigation, e.g. to cancel a pending debounced search so
   * an explicit page move can't be clobbered later by a stale callback.
   */
  onBeforeNavigate?: () => void;
}

/**
 * Page-replacing cursor pagination for a Relay connection rendered in a grid.
 *
 * Owns the page index, page size, pending-transition state, the 1-based range
 * summary and the next/previous/page-size handlers — everything the
 * {@link StackletGrid} pagination footer needs. The actual fetch is delegated
 * to the caller's `onNavigate`, which keeps the hook agnostic of the specific
 * query and lets each caller layer on its own filter variables and fetch
 * policy.
 *
 * "Page-replacing" means each move fetches a fresh window with explicit forward
 * (`first`/`after`) or backward (`last`/`before`) cursors, rather than appending
 * to a `@connection`-backed list.
 */
export function useCursorPagination({
  pageInfo,
  rowCount,
  defaultPageSize,
  onNavigate,
  onBeforeNavigate,
}: UseCursorPaginationParams) {
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [pageIndex, setPageIndex] = useState(0);
  const [isPending, startTransition] = useTransition();

  const goToPage = useCallback(
    (variables: CursorVariables) => {
      onBeforeNavigate?.();
      // Keep the current page visible (dimmed) rather than dropping to the
      // Suspense boundary while the next one loads.
      startTransition(() => onNavigate(variables));
    },
    [onNavigate, onBeforeNavigate],
  );

  /** Jump back to the first page, e.g. after a search or page-size change. */
  const goToFirstPage = useCallback(
    (size = pageSize) => {
      setPageIndex(0);
      goToPage({ first: size, last: null, after: null, before: null });
    },
    [goToPage, pageSize],
  );

  const handlePageSizeChange = useCallback(
    (size: number) => {
      setPageSize(size);
      goToFirstPage(size);
    },
    [goToFirstPage],
  );

  const handleNext = useCallback(() => {
    setPageIndex((index) => index + 1);
    goToPage({
      first: pageSize,
      last: null,
      after: pageInfo.endCursor ?? null,
      before: null,
    });
  }, [goToPage, pageSize, pageInfo.endCursor]);

  const handlePrevious = useCallback(() => {
    setPageIndex((index) => Math.max(0, index - 1));
    goToPage({
      first: null,
      last: pageSize,
      after: null,
      before: pageInfo.startCursor ?? null,
    });
  }, [goToPage, pageSize, pageInfo.startCursor]);

  // Range covers the real rows on the current page; callers exclude any
  // synthetic rows (e.g. section headers) from rowCount.
  const rangeStart = rowCount === 0 ? 0 : pageIndex * pageSize + 1;
  const rangeEnd = pageIndex * pageSize + rowCount;

  return {
    pageSize,
    pageIndex,
    isPending,
    rangeStart,
    rangeEnd,
    handleNext,
    handlePrevious,
    handlePageSizeChange,
    goToFirstPage,
  };
}
